import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  label,
  placeholder = 'Sélectionner...',
  disabled = false,
  getOptionLabel = (opt) => opt.label || opt.designation || opt.name || String(opt),
  getOptionValue = (opt) => opt.value || opt.id || String(opt),
  className = '',
  maxHeight = 240,
  icon = null,
  clearable = true,
  error = '',
}) => {
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const ref = useRef();
  const dropdownRef = useRef();

  // Filter options based on search
  const filteredOptions = options.filter(opt =>
    getOptionLabel(opt).toLowerCase().includes(search.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Calculate dropdown position when opening
  const handleOpen = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpen(true);
  };

  // Handle selection
  const handleSelect = (opt) => {
    const val = getOptionValue(opt);
    if (value.includes(val)) {
      onChange(value.filter(v => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  // Remove one selected
  const handleRemove = (val) => {
    onChange(value.filter(v => v !== val));
  };

  // Remove all
  const handleClear = () => {
    onChange([]);
  };

  // Render selected chips
  const renderChips = () => (
    <div className="flex flex-wrap gap-1">
      {value.map(val => {
        const opt = options.find(o => getOptionValue(o) === val);
        if (!opt) return null;
        return (
          <span
            key={val}
            className={`flex items-center rounded-full px-2 py-0.5 text-xs font-medium mr-1 mb-1 shadow-sm
              ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-primary-100 text-primary-700'}`}
          >
            {getOptionLabel(opt)}
            <button
              type="button"
              className={`ml-1 focus:outline-none ${isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-primary-400 hover:text-primary-700'}`}
              onClick={e => {
                e.stopPropagation();
                handleRemove(val);
              }}
              tabIndex={-1}
            >
              <i className="fas fa-times text-xs"></i>
            </button>
          </span>
        );
      })}
    </div>
  );

  // Dropdown content
  const dropdown = (
    <div
      ref={dropdownRef}
      className={`z-[9999] rounded-xl shadow-2xl py-2 px-2
        ${isDarkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-gray-200'}`}
      style={{
        position: 'absolute',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        maxHeight,
        overflowY: 'auto'
      }}
    >
      <div className="sticky top-0 z-10 bg-inherit pb-2">
        <input
          type="text"
          className={`w-full px-4 py-3 border rounded-lg text-base focus:ring-2 focus:ring-primary-500 focus:border-transparent
            ${isDarkMode
              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      {filteredOptions.length === 0 && (
        <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'} text-sm px-2 py-4 text-center`}>
          <i className="fas fa-search mr-2"></i>
          Aucun résultat
        </div>
      )}
      <div className="flex flex-col gap-1">
        {filteredOptions.map(opt => {
          const val = getOptionValue(opt);
          const selected = value.includes(val);
          return (
            <button
              type="button"
              key={val}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                selected
                  ? isDarkMode
                    ? 'bg-gray-700 text-primary-400 font-semibold'
                    : 'bg-primary-50 text-primary-700 font-semibold'
                  : isDarkMode
                  ? 'hover:bg-gray-700 text-gray-200'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
              onClick={() => handleSelect(opt)}
              tabIndex={0}
            >
              <span className="flex-1 truncate">{getOptionLabel(opt)}</span>
              {selected && (
                <i className={`fas fa-check ml-2 ${isDarkMode ? 'text-primary-400' : 'text-primary-500'}`}></i>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && (
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : ''}`}>
          {icon && <i className={`${icon} mr-2 ${isDarkMode ? 'text-primary-400' : 'text-primary-400'}`}></i>}
          {label}
        </label>
      )}
      <button
        type="button"
        disabled={disabled}
        className={`w-full min-h-[44px] text-left px-3 py-2 border rounded-lg transition-all focus:ring-2 focus:ring-primary-500 focus:border-transparent flex flex-wrap items-center gap-1
          ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}
          ${open ? 'ring-2 ring-primary-500 border-primary-400' : ''}
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
          ${error ? 'border-red-400' : ''}`}
        onClick={() => !disabled && (open ? setOpen(false) : handleOpen())}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value.length === 0 ? (
          <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{placeholder}</span>
        ) : (
          renderChips()
        )}
        <span className="ml-auto flex items-center gap-1">
          {clearable && value.length > 0 && (
            <button
              type="button"
              className={`${isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-500'} px-1`}
              onClick={e => {
                e.stopPropagation();
                handleClear();
              }}
              tabIndex={-1}
              title="Effacer la sélection"
            >
              <i className="fas fa-times-circle"></i>
            </button>
          )}
          <i className={`fas fa-chevron-down text-xs ml-1 transition-transform ${open ? 'rotate-180' : ''}`}></i>
        </span>
      </button>
      {open && createPortal(dropdown, document.body)}
      {error && (
        <div className="text-xs text-red-600 mt-1">{error}</div>
      )}
    </div>
  );
};

export default MultiSelect;